import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Card,
	Flex,
	Group,
	Modal,
	Select,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
	Textarea,
} from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { useForm } from '@mantine/form'
import {
	IconBook2,
	IconCalendar,
	IconCircleCheck,
	IconEye,
	IconEyeOff,
	IconPencil,
	IconPlus,
	IconSearch,
	IconTrash,
	IconUsers,
} from '@tabler/icons-react'
import axios from 'axios'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	CourseAiCourse,
	CourseAiSkill,
	CourseAiTopic,
	CourseAiTopicStat,
	createDictationTopic,
	createWritingTopic,
	deleteDictationTopic,
	deleteWritingTopic,
	getCourseAiList,
	getCourseAiTopicStat,
	getDictationTopics,
	getWritingTopics,
	unwrapList,
	unwrapObject,
	updateDictationTopic,
	updateWritingTopic,
} from 'src/apis/courseAiAPI'
import { uploadAudio } from 'src/apis/uploadApi'
import AudioFileCard from 'src/components/BookUniVini/AudioFileCard'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomProgress from 'src/components/CustomComponents/CustomProgress'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { BookAudioItem } from 'src/interfaces'
import {
	AUDIO_DROPZONE_ACCEPT,
	pickAudioFiles,
} from 'src/utils/audioDropzone'
import { getFilenameFromUrl } from 'src/utils/fileUrl'
import { lessonHtmlToText, looksLikeHtml } from 'src/utils/lessonHtml'
import { color, formatDate, size } from 'src/variable/constant'
import type { CourseAiTabValue } from './CourseAiPage'
import {
	classifyDictationLength,
	countWords,
	findDictationLengthIssues,
} from './dictationLength'
import {
	DictationLengthIndicator,
	DictationLengthListModal,
	DictationLengthSaveModal,
	DictationSkippedLabel,
	DictationWordCounter,
} from './DictationLengthWarnings'
import LessonHtmlPreview from './LessonHtmlPreview'
import {
	DICTATION_LIST_LIMIT,
	EditableSkill,
	liveLessonsOfCourse,
	matchCourseBySkill,
	normalizeText,
} from './lessonCounts'

type LessonFormValues = {
	title: string
	level: string
	topic: string
	original_text: string
	audio_url: string
	visibility: string
}

const LEVEL_OPTIONS = [
	{ value: 'a1', label: 'A1' },
	{ value: 'a2', label: 'A2' },
	{ value: 'b1', label: 'B1' },
	{ value: 'b2', label: 'B2' },
	{ value: 'c1', label: 'C1' },
	{ value: 'c2', label: 'C2' },
	{ value: 'native', label: 'Native' },
]

const SORT_OPTIONS = [
	{ value: 'day_asc', label: 'Day (1 → 90)' },
	{ value: 'newest', label: 'Newest' },
	{ value: 'oldest', label: 'Oldest' },
	{ value: 'title_asc', label: 'Title A→Z' },
]

const VISIBILITY_OPTIONS = [
	{ value: 'public', label: 'Public' },
	{ value: 'invisible', label: 'Invisible' },
]

const formatPercent = (part: number, total: number) => {
	if (!total || total <= 0) return null
	return `${((part / total) * 100).toFixed(1)}%`
}

const resolveLevelBadgeColor = (levelRaw: string) => {
	const level = levelRaw.toUpperCase()
	if (level === 'A1' || level.startsWith('A1')) return 'green'
	if (level === 'A2' || level.startsWith('A2')) return 'blue'
	if (level === 'B1' || level.startsWith('B1')) return 'cyan'
	if (level === 'B2' || level.startsWith('B2')) return 'indigo'
	if (level.startsWith('C')) return 'violet'
	return 'gray'
}

const emptyForm: LessonFormValues = {
	title: '',
	level: 'a1',
	topic: '',
	original_text: '',
	audio_url: '',
	visibility: 'public',
}

const resolveVisibility = (topic: CourseAiTopic): 'public' | 'invisible' => {
	if (typeof topic.is_active === 'boolean') {
		return topic.is_active ? 'public' : 'invisible'
	}
	if (typeof topic.is_public === 'boolean') {
		return topic.is_public ? 'public' : 'invisible'
	}
	const raw = normalizeText(topic.visibility)
	if (raw.includes('invisible') || raw === 'private' || raw === 'hidden') {
		return 'invisible'
	}
	if (raw === 'public') return 'public'
	return 'public'
}

const resolveDay = (topic: CourseAiTopic, index: number) => {
	const day = Number(
		topic.day_index ??
			topic.day ??
			topic.order ??
			topic.day_number ??
			topic.prototype_day_index,
	)
	return Number.isFinite(day) && day > 0 ? day : index + 1
}

// Counts the same text the edit form opens with, so the list and the form agree.
const listDictationLengthIssues = (topics: CourseAiTopic[]) =>
	findDictationLengthIssues(
		topics.map((topic, index) => ({
			id: topic.id,
			day: resolveDay(topic, index),
			title: topic.title,
			text: topic.original_text || topic.topic,
		})),
	)

const formatStatDate = (value?: string | null) => {
	if (!value) return '—'
	const parsed = dayjs(value)
	return parsed.isValid() ? parsed.format(formatDate) : '—'
}

const EMPTY_CELL = '\u2014'

// A lesson kept as HTML reads as plain text in a list line, with its blocks one space apart (UD-618).
const oneLine = (value?: string | null) =>
	lessonHtmlToText(value).replace(/\s*\n+\s*/g, ' ')

type LessonManagementPanelProps = {
	skill: CourseAiTabValue
	/** Called with the number of lessons of this course each time the list is read (the tab badge, UD-619). */
	onLessonCount?: (skill: EditableSkill, count: number) => void
}

function LessonManagementPanel({ skill, onLessonCount }: LessonManagementPanelProps) {
	const editableSkill = skill as EditableSkill
	const onLessonCountRef = useRef(onLessonCount)
	onLessonCountRef.current = onLessonCount
	const matchedCourseIdRef = useRef('')
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()

	const [courses, setCourses] = useState<CourseAiCourse[]>([])
	const [courseId, setCourseId] = useState<string>('')
	const [topics, setTopics] = useState<CourseAiTopic[]>([])
	const [stats, setStats] = useState<CourseAiTopicStat | null>(null)
	const [loading, setLoading] = useState(false)
	const [search, setSearch] = useState('')
	const [levelFilter, setLevelFilter] = useState<string | null>('all')
	const [sortBy, setSortBy] = useState<string | null>('day_asc')
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [modalOpen, setModalOpen] = useState(false)
	const [viewOpen, setViewOpen] = useState(false)
	const [editingTopic, setEditingTopic] = useState<CourseAiTopic | null>(null)
	const [viewingTopic, setViewingTopic] = useState<CourseAiTopic | null>(null)
	const [audioProgress, setAudioProgress] = useState(0)
	const [audioFileSize, setAudioFileSize] = useState<number | null>(null)
	const [uploadingAudio, setUploadingAudio] = useState(false)
	const [lengthListOpen, setLengthListOpen] = useState(false)
	const [pendingSave, setPendingSave] = useState<LessonFormValues | null>(null)
	// The course whose list already showed its length warning since this panel was opened.
	const lengthCheckedCourseRef = useRef<string | null>(null)

	const form = useForm<LessonFormValues>({
		initialValues: emptyForm,
		validate: {
			title: (value) => (value.trim() ? null : 'Title is required'),
			level: (value) => (value ? null : 'Level is required'),
			topic: (value) =>
				editableSkill === 'writing' && !value.trim()
					? 'Content is required'
					: null,
			original_text: (value) =>
				editableSkill === 'dictation' && !value.trim()
					? 'English text is required'
					: null,
			audio_url: (value) =>
				editableSkill === 'dictation' && !value.trim()
					? 'Audio file is required'
					: null,
		},
		validateInputOnBlur: true,
	})

	const audioPreviewItem = useMemo((): BookAudioItem | null => {
		const url = form.values.audio_url.trim()
		if (!url) return null
		return {
			id: editingTopic?.id || 'new-audio',
			url,
			filename: getFilenameFromUrl(url),
			file_size: audioFileSize,
		}
	}, [audioFileSize, editingTopic?.id, form.values.audio_url])

	const resetAudioUploadState = useCallback(() => {
		setAudioProgress(0)
		setAudioFileSize(null)
		setUploadingAudio(false)
	}, [])

	const loadCourses = useCallback(async () => {
		try {
			const response = await getCourseAiList({ limit: 100, offset: 0 })
			const { rows } = unwrapList(response)
			setCourses(rows as CourseAiCourse[])
			const matched = matchCourseBySkill(rows as CourseAiCourse[], editableSkill)
			matchedCourseIdRef.current = matched?.id || ''
			setCourseId(matched?.id || '')
		} catch (error) {
			openError(error)
		}
	}, [editableSkill, openError])

	const loadTopics = useCallback(async () => {
		if (!courseId) {
			setTopics([])
			return
		}
		setLoading(true)
		try {
			const response =
				editableSkill === 'writing'
					? await getWritingTopics({ course_id: courseId })
					: await getDictationTopics({
							course_id: courseId,
							limit: DICTATION_LIST_LIMIT,
						})
			const { rows } = unwrapList(response)
			const filteredByCourse = liveLessonsOfCourse(rows as CourseAiTopic[], courseId)
			setTopics(filteredByCourse)
			// The tab badge counts the course of this skill, not another one picked in the Course list.
			if (courseId === matchedCourseIdRef.current) {
				onLessonCountRef.current?.(editableSkill, filteredByCourse.length)
			}
			// The length warning opens once per list opened, not again after each save or delete.
			if (
				editableSkill === 'dictation' &&
				lengthCheckedCourseRef.current !== courseId
			) {
				lengthCheckedCourseRef.current = courseId
				if (listDictationLengthIssues(filteredByCourse).length > 0) {
					setLengthListOpen(true)
				}
			}
		} catch (error) {
			openError(error)
			setTopics([])
		} finally {
			setLoading(false)
		}
	}, [courseId, editableSkill, openError])

	const loadStats = useCallback(async () => {
		if (!courseId) {
			setStats(null)
			return
		}
		try {
			const response = await getCourseAiTopicStat(courseId)
			const object = unwrapObject<CourseAiTopicStat>(response)
			setStats(object)
		} catch (error) {
			openError(error)
			setStats(null)
		}
	}, [courseId, openError])

	useEffect(() => {
		loadCourses()
	}, [loadCourses])

	useEffect(() => {
		setPagination((previous) => ({ ...previous, pageIndex: 1 }))
		loadTopics()
		loadStats()
	}, [loadTopics, loadStats])

	const processedTopics = useMemo(() => {
		const keyword = search.trim().toLowerCase()
		let rows = topics.map((topic, index) => ({
			...topic,
			_day: resolveDay(topic, index),
			_visibility: resolveVisibility(topic),
			_learners: Number(
				topic.learners_count ?? topic.leaners_count ?? 0,
			),
		}))

		if (keyword) {
			rows = rows.filter((topic) => {
				const blob = [
					topic.title,
					topic.topic,
					topic.original_text,
					topic.level,
				]
					.join(' ')
					.toLowerCase()
				return blob.includes(keyword)
			})
		}

		if (levelFilter && levelFilter !== 'all') {
			rows = rows.filter(
				(topic) => normalizeText(topic.level) === normalizeText(levelFilter),
			)
		}

		rows = [...rows].sort((a, b) => {
			if (sortBy === 'newest') {
				return dayjs(b.updated_at || b.created_at).valueOf() -
					dayjs(a.updated_at || a.created_at).valueOf()
			}
			if (sortBy === 'oldest') {
				return dayjs(a.updated_at || a.created_at).valueOf() -
					dayjs(b.updated_at || b.created_at).valueOf()
			}
			if (sortBy === 'title_asc') {
				return String(a.title || '').localeCompare(String(b.title || ''))
			}
			return a._day - b._day
		})

		return rows.map((topic, index) => ({
			...topic,
			no: index + 1,
		}))
	}, [topics, search, levelFilter, sortBy])

	const lengthIssues = useMemo(
		() =>
			editableSkill === 'dictation' ? listDictationLengthIssues(topics) : [],
		[editableSkill, topics],
	)

	const pagedTopics = useMemo(() => {
		const start = (pagination.pageIndex - 1) * pagination.pageSize
		return processedTopics.slice(start, start + pagination.pageSize)
	}, [processedTopics, pagination])

	const closeEditor = useCallback(() => {
		setModalOpen(false)
		setEditingTopic(null)
		form.reset()
		resetAudioUploadState()
	}, [form, resetAudioUploadState])

	const openCreate = useCallback(() => {
		if (!courseId) {
			openModal({
				type: 'warning',
				label: 'Could not map a course_id for this tab. Check the Course AI list.',
			})
			return
		}
		setEditingTopic(null)
		form.setValues(emptyForm)
		resetAudioUploadState()
		setModalOpen(true)
	}, [courseId, form, openModal, resetAudioUploadState])

	const openEdit = useCallback(
		(topic: CourseAiTopic) => {
			setEditingTopic(topic)
			form.setValues({
				title: topic.title || '',
				level: normalizeText(topic.level) || 'a1',
				topic: topic.topic || '',
				original_text:
					topic.original_text ||
					(editableSkill === 'dictation' ? topic.topic || '' : ''),
				audio_url: topic.audio_url || '',
				visibility: resolveVisibility(topic),
			})
			resetAudioUploadState()
			setModalOpen(true)
		},
		[editableSkill, form, resetAudioUploadState],
	)

	const openView = useCallback((topic: CourseAiTopic) => {
		setViewingTopic(topic)
		setViewOpen(true)
	}, [])

	const handleRemoveAudio = useCallback(() => {
		form.setFieldValue('audio_url', '')
		resetAudioUploadState()
	}, [form, resetAudioUploadState])

	const handleUploadAudio = useCallback(
		async (files: File[]) => {
			const file = pickAudioFiles(files)[0]
			if (!file) {
				openError(new Error('Only audio files are allowed (mp3, wav, m4a, ...)'))
				return
			}

			setUploadingAudio(true)
			setAudioProgress(0)
			setAudioFileSize(file.size)

			try {
				const audioUrl = await uploadAudio(file, (event) => {
					const { loaded, total } = event
					if (total) {
						setAudioProgress(Math.round((loaded * 100) / total))
					}
				})

				if (!audioUrl) {
					openError(new Error('Upload failed — no URL returned'))
					setAudioFileSize(null)
					return
				}

				form.setFieldValue('audio_url', audioUrl)
				setAudioProgress(100)
			} catch (error) {
				if (!axios.isCancel(error)) {
					openError(error)
				}
				setAudioFileSize(null)
			} finally {
				setUploadingAudio(false)
			}
		},
		[form, openError],
	)

	const handleRejectAudio = useCallback(
		(files: File[]) => {
			const audioFiles = pickAudioFiles(files)
			if (audioFiles.length) {
				void handleUploadAudio(audioFiles)
				return
			}

			openError(new Error('Only audio files are allowed (mp3, wav, m4a, ...)'))
		},
		[handleUploadAudio, openError],
	)

	const submit = useCallback(
		async (values: LessonFormValues) => {
			if (!courseId) return
			openLoadingContext()
			try {
				let response
				const isActive = values.visibility === 'public'
				if (editableSkill === 'writing') {
					const body = {
						course_id: courseId,
						title: values.title.trim(),
						level: values.level,
						topic: values.topic.trim(),
						visibility: values.visibility,
						is_active: isActive,
					}
					response = editingTopic
						? await updateWritingTopic(editingTopic.id, body)
						: await createWritingTopic(body)
				} else {
					// Dictation = 1 English text (answer key) + 1 audio.
					// `topic` has ~500-char limit on BE — keep a short instruction there.
					const englishText = values.original_text.trim()
					const body = {
						course_id: courseId,
						title: values.title.trim(),
						level: values.level,
						topic: 'Listen and type what you hear.',
						original_text: englishText,
						audio_url: values.audio_url.trim(),
						is_active: isActive,
					}
					response = editingTopic
						? await updateDictationTopic(editingTopic.id, body)
						: await createDictationTopic(body)
				}

				if (response?.data?.code === 200) {
					closeEditor()
					await Promise.all([loadTopics(), loadStats()])
					openModal({
						type: 'success',
						label: `Lesson has been ${editingTopic ? 'updated' : 'created'} successfully`,
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			closeEditor,
			closeLoadingContext,
			courseId,
			editableSkill,
			editingTopic,
			loadStats,
			loadTopics,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	// A Listening text outside the allowed length asks first; the save is never refused.
	const handleSubmit = useCallback(
		(values: LessonFormValues) => {
			if (
				editableSkill === 'dictation' &&
				classifyDictationLength(countWords(values.original_text)) !== 'ok'
			) {
				setPendingSave(values)
				return
			}
			void submit(values)
		},
		[editableSkill, submit],
	)

	const saveAnyway = useCallback(() => {
		if (!pendingSave) return
		const values = pendingSave
		setPendingSave(null)
		void submit(values)
	}, [pendingSave, submit])

	const remove = useCallback(
		(topic: CourseAiTopic) => {
			openModal({
				type: 'confirm',
				label: `Delete Day ${resolveDay(topic, 0)}: "${oneLine(topic.title) || 'Untitled'}"? The Day 1→90 sequence is fixed for all users.`,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						const response =
							editableSkill === 'writing'
								? await deleteWritingTopic(topic.id)
								: await deleteDictationTopic(topic.id)
						if (response?.data?.code === 200) {
							await Promise.all([loadTopics(), loadStats()])
							openModal({
								type: 'success',
								label: 'Lesson has been deleted successfully',
							})
						}
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		[
			closeLoadingContext,
			editableSkill,
			loadStats,
			loadTopics,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const columns = useMemo((): MRT_ColumnDef[] => {
		return [
			{
				header: 'No.',
				accessorKey: 'no',
				size: size.no,
			},
			{
				header: 'Lesson Title',
				accessorKey: 'title',
				size: size.medium,
				Cell: ({ row }: any) => (
					<Text fw={600} sx={{ whiteSpace: 'normal' }}>
						{oneLine(row.original.title) || EMPTY_CELL}
						{editableSkill === 'dictation' && (
							<DictationSkippedLabel reason={row.original.skip_reason} />
						)}
					</Text>
				),
			},
			{
				header: 'Level',
				accessorKey: 'level',
				size: size.no,
				Cell: ({ cell }: any) => {
					const level = String(cell.getValue() || '').toUpperCase()
					return level ? (
						<Badge color={resolveLevelBadgeColor(level)} variant="light">
							{level}
						</Badge>
					) : (
						'—'
					)
				},
			},
			{
				header: editableSkill === 'dictation' ? 'English text' : 'Content',
				accessorKey: editableSkill === 'dictation' ? 'original_text' : 'topic',
				size: size.long,
				Cell: ({ row }: any) => (
					<Text size="sm" color="dimmed" lineClamp={2} sx={{ whiteSpace: 'normal' }}>
						{editableSkill === 'dictation'
							? row.original.original_text || row.original.topic || '—'
							: oneLine(row.original.topic) || EMPTY_CELL}
					</Text>
				),
			},
			{
				header: 'Visibility',
				accessorKey: '_visibility',
				size: size.text,
				Cell: ({ row }: any) =>
					row.original._visibility === 'public' ? (
						<Badge color="green" variant="light">
							Public
						</Badge>
					) : (
						<Badge color="orange" variant="light">
							Invisible
						</Badge>
					),
			},
			{
				header: 'Learners',
				accessorKey: '_learners',
				size: size.no,
			},
			{
				header: 'Updated',
				accessorKey: 'updated_at',
				size: size.text,
				Cell: ({ row }: any) =>
					formatStatDate(row.original.updated_at || row.original.created_at),
			},
		]
	}, [editableSkill])

	const selectedCourse = useMemo(
		() => courses.find((course) => course.id === courseId) || null,
		[courses, courseId],
	)

	// Count from the live lesson list (already excludes deleted_at).
	// topic-stat can still include soft-deleted rows, so do not prefer it here.
	const totalLessons = processedTopics.length
	const publicCount = processedTopics.filter(
		(topic) => topic._visibility === 'public',
	).length
	const invisibleCount = processedTopics.filter(
		(topic) => topic._visibility === 'invisible',
	).length
	const learnersCount = Number(stats?.leaners_count ?? 0)

	const statCards = [
		{
			label: 'Total Lessons',
			value: totalLessons,
			hint: `${editableSkill === 'writing' ? 'Writing' : 'Listening'} course`,
			icon: IconBook2,
			color: '#1f7a44',
			background: '#ecfdf3',
			border: '#abe0c1',
		},
		{
			label: 'Public',
			value: publicCount,
			hint: formatPercent(publicCount, totalLessons) || undefined,
			icon: IconCircleCheck,
			color: '#1f7a44',
			background: '#ecfdf3',
			border: '#abe0c1',
		},
		{
			label: 'Invisible',
			value: invisibleCount,
			hint: formatPercent(invisibleCount, totalLessons) || undefined,
			icon: IconEyeOff,
			color: '#c2410c',
			background: '#fff7ed',
			border: '#fdba74',
		},
		{
			label: 'Learners',
			value: learnersCount,
			hint: 'Learning',
			icon: IconUsers,
			color: '#1d4ed8',
			background: '#eff6ff',
			border: '#93c5fd',
		},
		{
			label: 'Last Updated',
			value: formatStatDate(stats?.last_updated),
			hint: undefined as string | undefined,
			icon: IconCalendar,
			color: '#7c3aed',
			background: '#f5f3ff',
			border: '#c4b5fd',
		},
	]

	return (
		<Stack spacing="md">
			<Flex gap={10} align="flex-end" wrap="wrap" justify="space-between">
				<Flex gap={10} align="flex-end" wrap="wrap" sx={{ flex: 1 }}>
					<TextInput
						placeholder="Search by lesson title..."
						icon={<IconSearch size={16} />}
						value={search}
						onChange={(event) => {
							setSearch(event.currentTarget.value)
							setPagination((previous) => ({ ...previous, pageIndex: 1 }))
						}}
						sx={{ minWidth: 240, flex: 1 }}
					/>
					<Select
						placeholder="Level"
						data={[{ value: 'all', label: 'Level: All' }, ...LEVEL_OPTIONS]}
						value={levelFilter}
						onChange={(value) => {
							setLevelFilter(value)
							setPagination((previous) => ({ ...previous, pageIndex: 1 }))
						}}
						sx={{ width: 160 }}
					/>
					<Select
						placeholder="Sort by"
						data={SORT_OPTIONS}
						value={sortBy}
						onChange={setSortBy}
						sx={{ width: 180 }}
					/>
					{courses.length > 0 && (
						<Select
							label="Course"
							placeholder="Select course"
							searchable
							data={courses.map((course) => ({
								value: course.id,
								label:
									course.title ||
									course.name ||
									course.slug ||
									course.id,
							}))}
							value={courseId || null}
							onChange={(value) => setCourseId(value || '')}
							sx={{ minWidth: 220 }}
						/>
					)}
				</Flex>
				<CustomButton
					variant="contained"
					onClick={openCreate}
					sx={{
						background: color.CmsPrimary,
						color: '#fff',
						boxShadow: 'none',
						':hover': {
							background: `${color.CmsPrimary} !important`,
							color: '#fff',
							boxShadow: 'none',
						},
					}}
				>
					<Group spacing={6}>
						<IconPlus size={18} />
						<span>Add Lesson</span>
					</Group>
				</CustomButton>
			</Flex>

			{!courseId && (
				<Text size="sm" color="orange">
					No course matched the{' '}
					<strong>{editableSkill}</strong> tab. Select a course manually from
					the dropdown or check `GET /course-ai`.
				</Text>
			)}

			{selectedCourse && (
				<Text size="xs" color="dimmed">
					Using course:{' '}
					<strong>
						{selectedCourse.title || selectedCourse.name || selectedCourse.id}
					</strong>
				</Text>
			)}

			<DictationLengthIndicator
				issues={lengthIssues}
				onOpen={() => setLengthListOpen(true)}
			/>

			<SimpleGrid
				cols={5}
				breakpoints={[
					{ maxWidth: 'lg', cols: 3 },
					{ maxWidth: 'sm', cols: 2 },
					{ maxWidth: 'xs', cols: 1 },
				]}
			>
				{statCards.map((card) => {
					const Icon = card.icon
					return (
						<Card
							key={card.label}
							withBorder
							radius="md"
							padding="md"
							sx={{
								borderColor: card.border,
								borderTop: `3px solid ${card.color}`,
							}}
						>
							<Flex align="flex-start" justify="space-between" gap={10}>
								<Stack spacing={2} sx={{ minWidth: 0 }}>
									<Text size="xs" color="dimmed" tt="uppercase" fw={700}>
										{card.label}
									</Text>
									<Text fw={800} size="xl" sx={{ lineHeight: 1.2 }}>
										{card.value}
										{card.hint &&
										card.label !== 'Total Lessons' &&
										card.label !== 'Learners' &&
										card.label !== 'Last Updated' ? (
											<Text
												component="span"
												size="sm"
												fw={600}
												color="dimmed"
												ml={8}
											>
												({card.hint})
											</Text>
										) : null}
									</Text>
									{card.hint &&
									(card.label === 'Total Lessons' ||
										card.label === 'Learners') ? (
										<Text size="xs" color="dimmed">
											{card.hint}
										</Text>
									) : null}
								</Stack>
								<div
									style={{
										width: 36,
										height: 36,
										borderRadius: 10,
										display: 'grid',
										placeItems: 'center',
										background: card.background,
										color: card.color,
										flexShrink: 0,
									}}
								>
									<Icon size={20} color={card.color} />
								</div>
							</Flex>
						</Card>
					)
				})}
			</SimpleGrid>

			<TableComponent
				columns={columns}
				total={processedTopics.length}
				data={pagedTopics}
				pagination={pagination}
				loading={loading}
				handleChangePage={(page: number) =>
					setPagination((previous) => ({ ...previous, pageIndex: page }))
				}
				handleChangePageSize={(pageSize: number) =>
					setPagination({ pageIndex: 1, pageSize })
				}
				_renderCustomActions={(row: any) => (
					<Group spacing={4} noWrap>
						<ActionIcon
							variant="subtle"
							color="gray"
							aria-label="View lesson"
							title="View"
							onClick={() => openView(row.original)}
						>
							<IconEye size={18} />
						</ActionIcon>
						<ActionIcon
							variant="subtle"
							color="green"
							aria-label="Edit lesson"
							title="Edit"
							onClick={() => openEdit(row.original)}
						>
							<IconPencil size={18} />
						</ActionIcon>
						<ActionIcon
							variant="subtle"
							color="red"
							aria-label="Delete lesson"
							title="Delete"
							onClick={() => remove(row.original)}
						>
							<IconTrash size={18} />
						</ActionIcon>
					</Group>
				)}
			/>

			<Modal
				opened={modalOpen}
				onClose={closeEditor}
				title={editingTopic ? 'Edit Lesson' : 'Add Lesson'}
				size={720}
				centered
			>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<TextInput
							withAsterisk
							label="Lesson title"
							placeholder="Introduce Yourself"
							{...form.getInputProps('title')}
						/>
						<Select
							withAsterisk
							label="Level"
							data={LEVEL_OPTIONS}
							{...form.getInputProps('level')}
						/>
						{editableSkill === 'writing' ? (
							<Stack spacing={8}>
								<Textarea
									withAsterisk
									label="Content (text or HTML)"
									placeholder="Write an introduction about yourself..."
									minRows={4}
									autosize
									{...form.getInputProps('topic')}
								/>
								{looksLikeHtml(form.values.topic) && (
									<Box
										data-testid="lesson-content-preview"
										sx={{
											border: '1px solid #e6ebe7',
											borderRadius: 8,
											background: '#fafcfa',
											padding: '10px 12px',
										}}
									>
										<Text size="xs" color="dimmed" mb={6}>
											Preview, as learners see it
										</Text>
										<LessonHtmlPreview html={form.values.topic} />
									</Box>
								)}
							</Stack>
						) : (
							<>
								<Stack spacing={4}>
									<Textarea
										withAsterisk
										label="English text"
										description="Transcript of the audio — used to check what the learner types"
										placeholder="Paste the English text that matches the audio..."
										minRows={5}
										autosize
										{...form.getInputProps('original_text')}
									/>
									<DictationWordCounter text={form.values.original_text} />
								</Stack>
								<Stack spacing={8}>
									<Text size="sm" fw={600}>
										Audio file
									</Text>
									<Text size="xs" color="dimmed">
										Same model as Book: 1 English audio + matching English text
									</Text>
									{audioProgress > 0 && audioProgress < 100 && (
										<CustomProgress value={audioProgress} />
									)}
									{audioPreviewItem ? (
										<AudioFileCard
											audio={audioPreviewItem}
											onRemove={handleRemoveAudio}
										/>
									) : (
										<Text size="sm" color="dimmed">
											No audio uploaded
										</Text>
									)}
									<Dropzone
										accept={AUDIO_DROPZONE_ACCEPT}
										onDrop={handleUploadAudio}
										onReject={(rejections) =>
											handleRejectAudio(rejections.map((item) => item.file))
										}
										multiple={false}
										disabled={uploadingAudio}
										sx={{
											border: '1px dashed #dbe6dc',
											borderRadius: 12,
											background: '#fafcfa',
											padding: 16,
										}}
									>
										<Text size="sm" color="dimmed" align="center">
											{audioPreviewItem
												? 'Drag and drop to replace audio or click to browse'
												: 'Drag and drop Tristan ENG mp3 or click to browse'}
										</Text>
										<Text size="xs" color="dimmed" align="center" mt={4}>
											mp3, wav, m4a, aac, ogg, webm
										</Text>
									</Dropzone>
									{form.errors.audio_url && (
										<Text size="xs" color="red">
											{form.errors.audio_url}
										</Text>
									)}
								</Stack>
							</>
						)}
						<Select
							label="Visibility"
							data={VISIBILITY_OPTIONS}
							{...form.getInputProps('visibility')}
						/>
						<Text size="xs" color="dimmed">
							{editableSkill === 'dictation'
								? 'Learner listens to the audio and types it back; English text is the answer key. Day sequence is fixed for all users.'
								: 'Content is plain text or HTML (no docx upload); HTML is saved as typed and shown formatted in the preview. Day sequence is fixed: all users share the same prompt per day.'}
						</Text>
						<Button
							variant="outline"
							type="submit"
							disabled={uploadingAudio}
						>
							{editingTopic ? 'Update lesson' : 'Create lesson'}
						</Button>
					</Stack>
				</form>
			</Modal>

			<Modal
				opened={viewOpen}
				onClose={() => {
					setViewOpen(false)
					setViewingTopic(null)
				}}
				title={oneLine(viewingTopic?.title) || 'Lesson detail'}
				size={640}
				centered
			>
				{viewingTopic && (
					<Stack spacing="sm">
						<Text size="sm">
							<strong>Day:</strong> {resolveDay(viewingTopic, 0)}
						</Text>
						<Text size="sm">
							<strong>Level:</strong>{' '}
							{String(viewingTopic.level || '—').toUpperCase()}
						</Text>
						<Text size="sm">
							<strong>Visibility:</strong> {resolveVisibility(viewingTopic)}
						</Text>
						{editableSkill === 'writing' ? (
							<Stack spacing={4}>
								<Text size="sm">
									<strong>Content:</strong>
								</Text>
								{looksLikeHtml(viewingTopic.topic) ? (
									<LessonHtmlPreview
										data-testid="lesson-view-content"
										html={viewingTopic.topic}
									/>
								) : (
									<Text size="sm" sx={{ whiteSpace: 'pre-wrap' }}>
										{viewingTopic.topic || EMPTY_CELL}
									</Text>
								)}
							</Stack>
						) : (
							<>
								<Text size="sm" sx={{ whiteSpace: 'pre-wrap' }}>
									<strong>English text:</strong>
									{'\n'}
									{viewingTopic.original_text || viewingTopic.topic || '—'}
								</Text>
								<Stack spacing={8}>
									<Text size="sm" fw={600}>
										Audio
									</Text>
									{viewingTopic.audio_url ? (
										<AudioFileCard
											audio={{
												id: viewingTopic.id,
												url: viewingTopic.audio_url,
												filename: getFilenameFromUrl(viewingTopic.audio_url),
											}}
										/>
									) : (
										<Text size="sm" color="dimmed">
											No audio
										</Text>
									)}
								</Stack>
							</>
						)}
					</Stack>
				)}
			</Modal>

			<DictationLengthListModal
				opened={lengthListOpen}
				issues={lengthIssues}
				onClose={() => setLengthListOpen(false)}
			/>
			{/* Mounted only while it asks: a modal that already exists when the form opens stays hidden from screen readers. */}
			{pendingSave !== null && (
				<DictationLengthSaveModal
					wordCount={countWords(pendingSave.original_text)}
					onSaveAnyway={saveAnyway}
					onGoBack={() => setPendingSave(null)}
				/>
			)}
		</Stack>
	)
}

export default React.memo(LessonManagementPanel)
